import { useUpdateTagFavoriteMutation } from '@Queries/index';
import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useErrorMessages, useUserSession } from '../../../../../models/hooks/appStateHooks';
import {
	Dropdown,
	DropdownContent,
	DropdownItem,
	DropdownRadioGroup,
	DropdownRadioItem,
	DropdownSeparator,
	DropdownTrigger,
} from '../../../../components-v2/Dropdown';
import { ButtonBase } from '../../../../components/Button';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import { ChevronDown } from '../../../../components/svgs/icons/ChevronDown';
import { styleSheet } from '../styles';
import { TagFavoriteForIndicator } from './TagFavoriteIndicator';

const favoriteForLabels: Record<Api.FavoriteFor, string> = {
	[Api.FavoriteFor.User]: 'Personal favorite',
	[Api.FavoriteFor.Account]: 'Shared favorite',
	[Api.FavoriteFor.None]: 'Not a favorite',
};

interface TagFavoriteColumnProps {
	tag: Api.IAccountTag;
	impersonationContext?: Api.IImpersonationContext;
	disabled?: boolean;
	refreshTags: () => void;
}

export const TagFavoriteColumn: React.FC<TagFavoriteColumnProps> = ({
	tag,
	impersonationContext,
	disabled = false,
	refreshTags,
}) => {
	const [isUpdating, setIsUpdating] = React.useState(false);
	const errorMessages = useErrorMessages();
	const userSession = useUserSession();

	const updateTagFavoriteMutation = useUpdateTagFavoriteMutation({
		impersonationContext,
		onSuccess: () => {
			setIsUpdating(false);
			refreshTags();
		},
		onError: (error: Api.IOperationResultNoValue) => {
			setIsUpdating(false);
			errorMessages.pushApiError(error);
		},
	});

	// Check if user has admin rights for this tag
	const isAdmin = userSession.userRole === 'admin' || userSession.userRole === 'superAdmin';

	// Determine if user can change favorite status
	const canChangeFavoriteFor =
		tag.favoriteFor !== Api.FavoriteFor.Account || (tag.favoriteFor === Api.FavoriteFor.Account && isAdmin);

	const toggleFavorite = () => {
		if (canChangeFavoriteFor) {
			const favoriteFor = tag.favoriteFor === Api.FavoriteFor.User ? Api.FavoriteFor.None : Api.FavoriteFor.User;
			saveFavoriteFor(favoriteFor);
		}
	};

	const saveFavoriteFor = (favoriteFor: Api.FavoriteFor) => {
		setIsUpdating(true);
		updateTagFavoriteMutation.mutate({
			tagId: tag.id,
			favoriteFor,
		});
	};

	const renderCurrentFavorite = () => {
		const isDisabled = !canChangeFavoriteFor;

		let title: string;
		if (isDisabled) {
			title = 'Shared favorite tag set by account admin';
		} else {
			switch (tag.favoriteFor) {
				case Api.FavoriteFor.Account:
					title = 'Shared favorite';
					break;
				case Api.FavoriteFor.User:
					title = 'Personal favorite';
					break;
				case Api.FavoriteFor.None:
				default:
					title = 'Click to make tag a favorite';
					break;
			}
		}

		return (
			<ButtonBase title={title} onClick={toggleFavorite} disabled={disabled || isUpdating}>
				<TagFavoriteForIndicator favoriteFor={tag.favoriteFor} />
			</ButtonBase>
		);
	};

	const renderFavoriteDropDown = () => {
		const label = `Favorite: ${favoriteForLabels[tag.favoriteFor]}`;
		return (
			<Dropdown disabled={disabled}>
				<DropdownTrigger
					aria-label={label}
					title={label}
					className={css(styleSheet.favoriteTrigger, disabled && styleSheet.cursorNotAllowed)}
				>
					<TagFavoriteForIndicator favoriteFor={tag.favoriteFor} />
					<ChevronDown className={css(styleSheet.favoriteTriggerCaret)} />
				</DropdownTrigger>
				<DropdownContent align='start' styles={[styleSheet.favoriteMenu]}>
					<DropdownRadioGroup
						value={tag.favoriteFor}
						onValueChange={value => saveFavoriteFor(value as Api.FavoriteFor)}
					>
						{[Api.FavoriteFor.User, Api.FavoriteFor.Account].map(favoriteFor => (
							<DropdownRadioItem key={favoriteFor} value={favoriteFor} styles={[styleSheet.favoriteMenuItem]}>
								<span className={css(styleSheet.favoriteMenuItemLabel)}>
									<TagFavoriteForIndicator favoriteFor={favoriteFor} />
									{favoriteForLabels[favoriteFor]}
								</span>
							</DropdownRadioItem>
						))}
					</DropdownRadioGroup>
					<DropdownSeparator />
					<DropdownItem
						disabled={tag.favoriteFor === Api.FavoriteFor.None}
						onClick={() => saveFavoriteFor(Api.FavoriteFor.None)}
						styles={[styleSheet.favoriteMenuItem, styleSheet.favoriteRemoveItem]}
					>
						<TagFavoriteForIndicator favoriteFor={Api.FavoriteFor.None} />
						Remove from favorites
					</DropdownItem>
				</DropdownContent>
			</Dropdown>
		);
	};

	if (isUpdating) {
		return (
			<div className={css(styleSheet.favoriteCol, styleSheet.favorite)}>
				<LoadingSpinner type='tiny' />
			</div>
		);
	}

	if (isAdmin) {
		return renderFavoriteDropDown();
	}

	return renderCurrentFavorite();
};
